import australiaStatesGeoJson from "./data/australia-states.json";
import {
  GEOJSON_STATE_NAME_TO_CODE,
  GEOGRAPHY_MAP_METRICS,
  getGeographyMetricValue,
  getStateHeatColor,
  getRevenueHeatColor,
} from "./tenantGeography.utils";

const STATE_MAP_PADDING = 32;

const CODE_TO_GEOJSON_NAME = Object.fromEntries(
  Object.entries(GEOJSON_STATE_NAME_TO_CODE).map(([name, code]) => [code, name])
);

export const normalizeSuburb = (suburb) => {
  if (!suburb) {
    return "";
  }

  return suburb.trim().replace(/\s+/g, " ").toUpperCase();
};

export const normalizePostcode = (postcode) => {
  if (!postcode) {
    return null;
  }

  const digits = String(postcode).replace(/\D/g, "");

  if (!digits) {
    return null;
  }

  return digits.padStart(4, "0");
};

export const regionKey = (postcode, suburb) => {
  const normalizedPostcode = normalizePostcode(postcode) || "unknown";
  const normalizedSuburb = normalizeSuburb(suburb) || "unknown";

  return `${normalizedPostcode}|${normalizedSuburb}`;
};

const isValidCoordinate = (lat, lng) => {
  const parsedLat = Number(lat);
  const parsedLng = Number(lng);

  return (
    Number.isFinite(parsedLat) &&
    Number.isFinite(parsedLng) &&
    parsedLat !== 0 &&
    parsedLng !== 0 &&
    parsedLat >= -44 &&
    parsedLat <= -9 &&
    parsedLng >= 112 &&
    parsedLng <= 155
  );
};

const collectGeometryCoordinates = (geometry, coordinates = []) => {
  if (!geometry) {
    return coordinates;
  }

  if (geometry.type === "Point") {
    coordinates.push(geometry.coordinates);
    return coordinates;
  }

  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach((ring) => {
      ring.forEach((point) => coordinates.push(point));
    });
    return coordinates;
  }

  if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((polygon) => {
      polygon.forEach((ring) => {
        ring.forEach((point) => coordinates.push(point));
      });
    });
  }

  return coordinates;
};

const projectStatePoint = ([lng, lat], bounds) => {
  const innerWidth = bounds.width - STATE_MAP_PADDING * 2;
  const innerHeight = bounds.height - STATE_MAP_PADDING * 2;
  const x =
    STATE_MAP_PADDING +
    ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng || 1)) * innerWidth;
  const y =
    STATE_MAP_PADDING +
    ((bounds.maxLat - lat) / (bounds.maxLat - bounds.minLat || 1)) * innerHeight;

  return [x, y];
};

const ringToStatePath = (ring, bounds) =>
  ring
    .map(([lng, lat], index) => {
      const [x, y] = projectStatePoint([lng, lat], bounds);
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ") + " Z";

const geometryToStatePath = (geometry, bounds) => {
  if (!geometry) {
    return "";
  }

  if (geometry.type === "Polygon") {
    return geometry.coordinates.map((ring) => ringToStatePath(ring, bounds)).join(" ");
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates
      .map((polygon) => polygon.map((ring) => ringToStatePath(ring, bounds)).join(" "))
      .join(" ");
  }

  return "";
};

export const findStateGeoJsonFeature = (stateCode) => {
  const targetName = CODE_TO_GEOJSON_NAME[stateCode];

  return (australiaStatesGeoJson.features || []).find(
    (feature) => feature?.properties?.STATE_NAME === targetName
  );
};

export const computeStateMapBounds = (stateCode, { width = 640, height = 520 } = {}) => {
  const feature = findStateGeoJsonFeature(stateCode);
  const points = collectGeometryCoordinates(feature?.geometry);

  if (!points.length) {
    return {
      width,
      height,
      minLng: 112.5,
      maxLng: 154.5,
      minLat: -44.5,
      maxLat: -9.5,
      viewBox: `0 0 ${width} ${height}`,
    };
  }

  const lngs = points.map(([lng]) => lng);
  const lats = points.map(([, lat]) => lat);
  const lngPad = (Math.max(...lngs) - Math.min(...lngs)) * 0.06 || 0.2;
  const latPad = (Math.max(...lats) - Math.min(...lats)) * 0.06 || 0.2;

  return {
    width,
    height,
    minLng: Math.min(...lngs) - lngPad,
    maxLng: Math.max(...lngs) + lngPad,
    minLat: Math.min(...lats) - latPad,
    maxLat: Math.max(...lats) + latPad,
    viewBox: `0 0 ${width} ${height}`,
  };
};

export const buildStateOutlinePath = (stateCode, bounds) => {
  const feature = findStateGeoJsonFeature(stateCode);

  return geometryToStatePath(feature?.geometry, bounds);
};

const POSTCODE_LOADERS = {
  ACT: () => import("./data/postcodes/ACT.json"),
  NSW: () => import("./data/postcodes/NSW.json"),
  NT: () => import("./data/postcodes/NT.json"),
  QLD: () => import("./data/postcodes/QLD.json"),
  SA: () => import("./data/postcodes/SA.json"),
  TAS: () => import("./data/postcodes/TAS.json"),
  VIC: () => import("./data/postcodes/VIC.json"),
  WA: () => import("./data/postcodes/WA.json"),
};

export const loadPostcodeCoordinateIndex = async (stateCode) => {
  const loader = POSTCODE_LOADERS[stateCode];

  if (!loader) {
    return { byRegionKey: {}, byPostcode: {} };
  }

  const module = await loader();
  const rows = module.default || module;
  const byRegionKey = {};
  const byPostcode = {};

  rows.forEach((row) => {
    if (!isValidCoordinate(row.lat, row.lon)) {
      return;
    }

    const key = regionKey(row.postcode, row.suburb);
    const coords = { lat: Number(row.lat), lng: Number(row.lon) };

    byRegionKey[key] = coords;

    const postcode = normalizePostcode(row.postcode);

    if (!postcode) {
      return;
    }

    if (!byPostcode[postcode]) {
      byPostcode[postcode] = { lat: 0, lng: 0, count: 0 };
    }

    byPostcode[postcode].lat += coords.lat;
    byPostcode[postcode].lng += coords.lng;
    byPostcode[postcode].count += 1;
  });

  Object.keys(byPostcode).forEach((postcode) => {
    const entry = byPostcode[postcode];
    entry.lat /= entry.count;
    entry.lng /= entry.count;
  });

  return { byRegionKey, byPostcode };
};

export const resolveRegionCoordinates = (region, coordinateIndex) => {
  const key = regionKey(region.postcode, region.suburb);
  const exact = coordinateIndex.byRegionKey[key];

  if (exact) {
    return exact;
  }

  const postcode = normalizePostcode(region.postcode);
  const fallback = postcode ? coordinateIndex.byPostcode[postcode] : null;

  if (fallback) {
    return { lat: fallback.lat, lng: fallback.lng };
  }

  return null;
};

export const getStateGeographyMetricMax = (
  stateGeography,
  metric = GEOGRAPHY_MAP_METRICS.TENANTS
) =>
  Math.max(
    0,
    ...(stateGeography?.regions || []).map((region) => getGeographyMetricValue(region, metric))
  );

export const buildStateRegionBubbles = (
  stateGeography,
  coordinateIndex,
  bounds,
  { metric = GEOGRAPHY_MAP_METRICS.TENANTS, revenueAvailable = false } = {}
) => {
  const maxValue = getStateGeographyMetricMax(stateGeography, metric);
  const isTenantMetric = metric === GEOGRAPHY_MAP_METRICS.TENANTS;

  return (stateGeography?.regions || [])
    .map((region) => {
      const coords = resolveRegionCoordinates(region, coordinateIndex);

      if (!coords) {
        return null;
      }

      const metricValue = getGeographyMetricValue(region, metric);
      const [x, y] = projectStatePoint([coords.lng, coords.lat], bounds);
      const radius = Math.max(6, Math.min(28, 6 + Math.sqrt(metricValue || 1) * 4));
      const fill =
        isTenantMetric || !revenueAvailable
          ? getStateHeatColor(metricValue, maxValue)
          : getRevenueHeatColor(metricValue, maxValue);

      return {
        regionKey: region.region_key,
        label: region.label,
        suburb: region.suburb,
        postcode: region.postcode,
        count: region.total || 0,
        metricValue,
        active: region.active || 0,
        suspended: region.suspended || 0,
        onTrial: region.on_trial || 0,
        payingTenants: region.paying_tenants || 0,
        estimatedMrrCents: region.estimated_mrr_cents || 0,
        collectedAllTimeCents: region.collected_all_time_cents || 0,
        tenantSharePct: region.tenant_share_pct || 0,
        revenueSharePct: region.revenue_share_pct || 0,
        x,
        y,
        radius,
        fill,
      };
    })
    .filter(Boolean);
};

export const formatStateGeographySummary = (stateGeography) => {
  const total = stateGeography?.total_tenants ?? 0;
  const regionCount = stateGeography?.region_count ?? 0;
  const stateName = stateGeography?.state_name || stateGeography?.state_code || "State";

  return `${total} tenant${total === 1 ? "" : "s"} across ${regionCount} area${
    regionCount === 1 ? "" : "s"
  } in ${stateName}`;
};

export const buildTenantsListPathForRegion = ({ stateCode, suburb, postcode }) => {
  const params = new URLSearchParams({ status: "all" });

  if (stateCode) {
    params.set("state", stateCode);
  }

  if (suburb) {
    params.set("suburb", suburb);
  }

  if (postcode) {
    params.set("postcode", postcode);
  }

  return `/tenants?${params.toString()}`;
};

export const buildGeographyViewPath = ({ stateCode = null } = {}) => {
  const params = new URLSearchParams({ view: "geography" });

  if (stateCode) {
    params.set("state", stateCode);
  }

  return `/tenants?${params.toString()}`;
};
